import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Form } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';

import { toast } from 'react-toastify';

import { useNavigate } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

import { confirmToast } from '../utils/toast';

/* Card accents, picked by hashing the category so a given category always
   wears the same colour rather than depending on its position in the grid. */
const CAT_TONES = [
  { from: '#c2410c', to: '#f59e0b', solid: '#ea580c' },
  { from: '#5b21b6', to: '#a855f7', solid: '#7c3aed' },
  { from: '#0e7490', to: '#22d3ee', solid: '#0891b2' },
  { from: '#15803d', to: '#4ade80', solid: '#16a34a' },
  { from: '#9f1239', to: '#fb7185', solid: '#e11d48' },
  { from: '#b45309', to: '#fcd34d', solid: '#d97706' }
];

const toneFor = (key = '') => {
  let hash = 0;

  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }

  return CAT_TONES[hash % CAT_TONES.length];
};

function AdminCategoriesPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');

  const [image, setImage] = useState('');

  const [categories, setCategories] = useState([]);

  const [editId, setEditId] = useState(null);

  const [uploading, setUploading] = useState(false);

  const [imageFile, setImageFile] = useState(null);

  const [fileInputKey, setFileInputKey] = useState(0);

  /* Object URLs have to be revoked, and must not be minted during render —
     doing it inline leaked a new URL on every keystroke. */
  const [filePreview, setFilePreview] = useState('');

  useEffect(() => {
    if (!imageFile) {
      setFilePreview('');

      return undefined;
    }

    const url = URL.createObjectURL(imageFile);

    setFilePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const removeImage = () => {
    setImage('');
    setImageFile(null);
    setFileInputKey((k) => k + 1);
  };

  /* Everything that leaves the form has to clear the picked file too,
     otherwise a stale upload keeps winning the preview. */
  const resetForm = () => {
    setName('');
    setImage('');
    setImageFile(null);
    setFileInputKey((k) => k + 1);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);

      setCategories(data);
    } catch (error) {
      console.log(error);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API}/categories`,

        {
          name,
          image
        }
      );

      toast.success('Category Added Successfully');

      resetForm();

      fetchCategories();
    } catch (error) {
      toast.error('Failed To Add Category');
    }
  };

  const editHandler = (category) => {
    setEditId(category._id);

    setName(category.name);

    setImage(category.image);

    /* drop any previously picked file, or its preview would mask the
       category's own image */
    setImageFile(null);
    setFileInputKey((k) => k + 1);

    window.scrollTo({
      top: 0,

      behavior: 'smooth'
    });
  };

  const updateHandler = async (e) => {
    e.preventDefault();

    try {
      await axios.put(
        `${API}/categories/${editId}`,

        {
          name,
          image
        }
      );

      toast.success('Category Updated Successfully');

      setEditId(null);

      resetForm();

      fetchCategories();
    } catch (error) {
      toast.error('Update Failed');
    }
  };

  const cancelEdit = () => {
    setEditId(null);

    resetForm();
  };

  const deleteHandler = async (id) => {
    if (!(await confirmToast('Delete Category?'))) {
      return;
    }

    try {
      await axios.delete(`${API}/categories/${id}`);

      toast.success('Category Deleted');

      fetchCategories();
    } catch (error) {
      toast.error('Delete Failed');
    }
  };
  const uploadHandler = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const formData = new FormData();

    formData.append('image', file);

    try {
      setUploading(true);

      const { data } = await axios.post(
        `${API}/upload`,

        formData,

        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImage(data.image);

      toast.success('Image Uploaded Successfully');
    } catch (error) {
      console.log(error);

      toast.error('Upload Failed');
    } finally {
      setUploading(false);
    }
  };

  const totalProducts = categories.reduce(
    (sum, c) => sum + (c.productCount || 0),
    0
  );
  const totalRevenue = categories.reduce((sum, c) => sum + (c.revenue || 0), 0);

  const previewSrc = filePreview || image;

  return (
    <>
      <CustomNavbar />

      <div className="acat-wrap">
        <Container>
          {/* Header */}

          <Card className="border-0 shadow-lg mb-4 acat-hero">
            <span className="acat-hero-aurora" aria-hidden="true" />
            <span className="acat-hero-blob acat-hero-blob-1" />
            <span className="acat-hero-blob acat-hero-blob-2" />

            {/* Motes drifting up through the banner */}
            <span className="acat-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="acat-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4 p-md-5">
              <h1 className="fw-bold mb-1 acat-hero-title">
                <span className="acat-hero-icon">📂</span>
                <span className="acat-hero-title-text">Manage Categories</span>
              </h1>

              <p className="mb-0 acat-hero-text">
                Create, edit and delete product categories.
              </p>

              <div className="acat-hero-stats">
                <div className="acat-hstat">
                  <span className="acat-hstat-value">{categories.length}</span>
                  <span className="acat-hstat-label">Categories</span>
                </div>

                <span className="acat-hstat-sep" />

                <div className="acat-hstat">
                  <span className="acat-hstat-value is-blue">
                    {totalProducts}
                  </span>
                  <span className="acat-hstat-label">Products</span>
                </div>

                <span className="acat-hstat-sep" />

                <div className="acat-hstat">
                  <span className="acat-hstat-value is-green">
                    ₹{totalRevenue.toLocaleString()}
                  </span>
                  <span className="acat-hstat-label">Revenue</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Form */}

          <Card
            className={`border-0 shadow-lg mb-5 acat-form-card overflow-hidden${
              editId ? ' is-editing' : ''
            }`}
          >
            <div className="acat-form-header d-flex align-items-center gap-3">
              <span className="acat-form-icon">{editId ? '✏️' : '➕'}</span>
              <h3 className="mb-0 fw-bold position-relative">
                {editId ? 'Edit Category' : 'Add Category'}
              </h3>
            </div>

            <Card.Body className="p-4">
              <Form onSubmit={editId ? updateHandler : submitHandler}>
                <Row className="g-4 align-items-start">
                  {/* Left: fields */}
                  <Col lg={8}>
                    <Form.Group className="mb-3">
                      <Form.Label className="acat-label">
                        🏷️ Category Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Headphones"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="acat-label">
                        🖼️ Image URL
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Paste an image URL"
                        value={image}
                        onChange={(e) => setImage(e.target.value)}
                      />
                    </Form.Group>

                    <div className="acat-or">
                      <span>OR UPLOAD</span>
                    </div>

                    <Form.Group>
                      <Form.Control
                        key={fileInputKey}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          setImageFile(e.target.files[0]);
                          uploadHandler(e);
                        }}
                      />
                      {uploading && (
                        <p className="mt-2 mb-0 acat-uploading">
                          <span className="acat-spinner" aria-hidden="true" />
                          Uploading…
                        </p>
                      )}
                    </Form.Group>

                    <div className="d-flex gap-2 mt-4 flex-wrap">
                      <button
                        type="submit"
                        className={`acat-submit${editId ? ' is-edit' : ''}`}
                      >
                        <span className="acat-submit-ico" aria-hidden="true">
                          {editId ? '✓' : '+'}
                        </span>
                        {editId ? 'Update Category' : 'Add Category'}
                      </button>

                      {editId && (
                        <button
                          type="button"
                          className="acat-cancel"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </Col>

                  {/* Right: live preview */}
                  <Col lg={4}>
                    <div className="acat-preview">
                      <div className="acat-preview-title">🖼️ Live Preview</div>

                      {previewSrc ? (
                        <>
                          <div className="acat-preview-img-box">
                            <img
                              src={previewSrc}
                              alt="Category Preview"
                              className="acat-preview-img"
                            />
                            <button
                              type="button"
                              className="acat-remove-btn"
                              title="Remove image"
                              onClick={removeImage}
                            >
                              ✕
                            </button>
                          </div>

                          {name && (
                            <div className="acat-preview-name">{name}</div>
                          )}

                          <button
                            type="button"
                            className="acat-remove-wide"
                            onClick={removeImage}
                          >
                            <span className="acat-act-ico" aria-hidden="true">
                              🗑
                            </span>
                            Remove Image
                          </button>
                        </>
                      ) : (
                        <div className="acat-preview-empty">
                          <span className="acat-preview-empty-ico">🖼️</span>
                          <small className="mt-2">No image selected</small>
                          <small className="text-muted">
                            Paste a URL or upload to preview
                          </small>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Categories */}

          {categories.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '18px' }}
            >
              <Card.Body>
                <div className="acat-empty-icon">🪔</div>
                <h5 className="fw-bold mt-2">No categories yet</h5>
                <p className="text-muted mb-0">
                  Add your first category using the form above.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {categories.map((category, index) => {
                const tone = toneFor(category._id || category.name || '');

                return (
                  <Col
                    xl={3}
                    lg={4}
                    md={6}
                    sm={6}
                    xs={12}
                    key={category._id}
                  >
                    <Card
                      className={`border-0 shadow-sm h-100 acat-card${
                        editId === category._id ? ' is-editing' : ''
                      }`}
                      style={{
                        '--c-from': tone.from,
                        '--c-to': tone.to,
                        '--c-accent': tone.solid,
                        /* capped so late cards don't wait seconds */
                        '--i': Math.min(index, 11)
                      }}
                    >
                      <div className="acat-card-img-wrap">
                        <Card.Img
                          variant="top"
                          src={category.image}
                          alt={category.name}
                          className="acat-card-img"
                        />
                        <div className="acat-card-overlay" />
                        <span className="acat-card-wash" aria-hidden="true" />
                        <h4 className="acat-card-title">{category.name}</h4>
                      </div>

                      <Card.Body className="d-flex flex-column p-3">
                        {/* Stats */}
                        <Row className="g-2 text-center mb-3 acat-stats">
                          <Col xs={4}>
                            <div className="acat-stat">
                              <div className="acat-stat-num">
                                {category.productCount || 0}
                              </div>
                              <div className="acat-stat-label">📦 Products</div>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <div className="acat-stat">
                              <div className="acat-stat-num is-green">
                                ₹{(category.revenue || 0).toLocaleString()}
                              </div>
                              <div className="acat-stat-label">💰 Revenue</div>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <div className="acat-stat">
                              <div className="acat-stat-num is-blue">
                                {category.orders || 0}
                              </div>
                              <div className="acat-stat-label">📋 Orders</div>
                            </div>
                          </Col>
                        </Row>

                        {/* Actions */}
                        <button
                          type="button"
                          className="acat-view-btn"
                          onClick={() =>
                            navigate(
                              `/admin/category-products/${category.name}`
                            )
                          }
                        >
                          <FaEye className="acat-view-ico" /> View Products
                        </button>

                        <div className="d-flex gap-2 mt-auto pt-2">
                          <button
                            type="button"
                            className="acat-act acat-act-edit flex-fill"
                            onClick={() => editHandler(category)}
                          >
                            <span className="acat-act-ico" aria-hidden="true">
                              ✏️
                            </span>
                            Edit
                          </button>

                          <button
                            type="button"
                            className="acat-act acat-act-del flex-fill"
                            onClick={() => deleteHandler(category._id)}
                          >
                            <span className="acat-act-ico" aria-hidden="true">
                              🗑
                            </span>
                            Delete
                          </button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{categoriesStyles}</style>
    </>
  );
}

const categoriesStyles = `
  .acat-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 40px) 0;
  }

  /* ===== Sanctum banner ===== */
  .acat-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 25px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 30%, #7c2d12 64%, #ea580c);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: acatHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes acatHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .acat-hero::before,
  .acat-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: acatThread 3.6s linear infinite;
  }
  .acat-hero::before { top: 0; }
  .acat-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes acatThread {
    to { background-position: 220% 0; }
  }

  .acat-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.40), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.38), transparent 62%);
    filter: blur(30px);
    animation: acatAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes acatAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .acat-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
    z-index: 0;
  }
  .acat-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 20%;
  }
  .acat-hero-blob-2 {
    width: 180px; height: 180px;
    background: #38bdf8;
    bottom: -90px; right: 8%;
    opacity: 0.3;
  }

  .acat-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .acat-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: acatMote 12s linear infinite;
  }
  .acat-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .acat-mote:nth-child(2) { left: 20%; animation-delay: 4.3s; width: 4px; height: 4px; }
  .acat-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .acat-mote:nth-child(4) { left: 46%; animation-delay: 7.2s; width: 5px; height: 5px; }
  .acat-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .acat-mote:nth-child(6) { left: 72%; animation-delay: 9.2s; width: 4px; height: 4px; }
  .acat-mote:nth-child(7) { left: 85%; animation-delay: 5.6s; }
  .acat-mote:nth-child(8) { left: 95%; animation-delay: 3.4s; width: 5px; height: 5px; }
  @keyframes acatMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-220px) translateX(22px) scale(1.1); }
  }

  .acat-hero .card-body { z-index: 2; }
  .acat-hero-title { display: flex; align-items: center; gap: 10px; }
  .acat-hero-icon {
    display: inline-block;
    animation: acatIconBob 3.2s ease-in-out infinite;
  }
  @keyframes acatIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-7deg); }
  }
  .acat-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: acatTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes acatTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .acat-hero-text { opacity: 0.85; }

  .acat-hero-stats {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: clamp(14px, 3vw, 28px);
    margin-top: 20px;
    padding: 13px clamp(18px, 3vw, 28px);
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(253, 230, 138, 0.3);
    backdrop-filter: blur(8px);
  }
  .acat-hstat { line-height: 1.15; }
  .acat-hstat-value {
    display: block;
    font-size: clamp(1.1rem, 3vw, 1.5rem);
    font-weight: 800;
    color: #fde68a;
  }
  .acat-hstat-value.is-green { color: #6ee7b7; }
  .acat-hstat-value.is-blue  { color: #93c5fd; }
  .acat-hstat-label {
    display: block;
    font-size: 0.64rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.75;
    margin-top: 3px;
  }
  .acat-hstat-sep {
    width: 1px;
    height: 30px;
    background: linear-gradient(180deg, transparent, rgba(253,230,138,0.5), transparent);
  }

  /* ===== Form card ===== */
  .acat-form-card {
    border-radius: 22px;
    border: 1px solid rgba(245, 158, 11, 0.16) !important;
  }
  /* Editing is a different mode — the card says so */
  .acat-form-card.is-editing {
    border-color: rgba(251, 191, 36, 0.7) !important;
    box-shadow: 0 18px 42px rgba(245, 158, 11, 0.24) !important;
  }
  .acat-form-header {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    background: linear-gradient(120deg, #3b0764, #7c2d12 55%, #ea580c);
    background-size: 220% 100%;
    color: #fff;
    padding: 18px 26px;
    animation: acatHeaderDrift 14s ease-in-out infinite alternate;
  }
  @keyframes acatHeaderDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  /* Gold thread under the header */
  .acat-form-header::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: acatThread 3.2s linear infinite;
  }
  .acat-form-icon {
    position: relative;
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: rgba(255,255,255,0.2);
    border: 1px solid rgba(253, 230, 138, 0.4);
    font-size: 1.4rem;
    animation: acatIconBob 3.4s ease-in-out infinite;
  }
  .acat-label {
    font-size: 0.8rem;
    font-weight: 800;
    color: #9a3412;
    letter-spacing: 0.3px;
    margin-bottom: 5px;
  }
  .acat-form-card .form-control {
    border-radius: 12px;
    padding: 11px 14px;
    border: 1px solid #eadfce;
    background: #fffdfa;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .acat-form-card .form-control:focus {
    background: #fff;
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234, 88, 12,0.15);
  }
  .acat-or {
    display: flex;
    align-items: center;
    text-align: center;
    color: #b45309;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 1px;
    margin: 10px 0 14px;
  }
  .acat-or::before,
  .acat-or::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #f2e3cd;
  }
  .acat-or span { padding: 0 12px; }

  .acat-uploading {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: 0.85rem;
    font-weight: 700;
    color: #b45309;
  }
  .acat-spinner {
    width: 15px; height: 15px;
    border-radius: 50%;
    border: 2px solid #fde68a;
    border-top-color: #ea580c;
    animation: acatSpin 0.8s linear infinite;
  }
  @keyframes acatSpin {
    to { transform: rotate(360deg); }
  }

  /* ===== Form buttons ===== */
  .acat-submit {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    border: none;
    border-radius: 12px;
    padding: 11px 26px;
    font-weight: 800;
    color: #fff;
    cursor: pointer;
    background: linear-gradient(135deg, #065f46, #16a34a 55%, #4ade80);
    box-shadow: 0 10px 24px rgba(22, 163, 74, 0.35);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, filter 0.25s ease;
  }
  .acat-submit.is-edit {
    color: #431407;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 10px 24px rgba(245, 158, 11, 0.42);
  }
  .acat-submit:hover { transform: translateY(-3px); filter: brightness(1.06); }
  .acat-submit:active { transform: translateY(-1px) scale(0.97); }
  .acat-submit-ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.22);
    font-size: 0.9rem;
    line-height: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .acat-submit:hover .acat-submit-ico { transform: rotate(180deg) scale(1.15); }

  .acat-cancel {
    border: 1px solid #e7e2da;
    border-radius: 12px;
    padding: 11px 22px;
    font-weight: 700;
    color: #78716c;
    background: #fff;
    cursor: pointer;
    transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease;
  }
  .acat-cancel:hover {
    transform: translateY(-2px);
    background: #f5f5f4;
    color: #44403c;
  }

  /* ===== Preview box ===== */
  .acat-preview {
    background: linear-gradient(180deg, #fffdf7, #fff7ea);
    border: 1.5px dashed #fcd34d;
    border-radius: 18px;
    padding: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .acat-preview-title {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #b45309;
    margin-bottom: 14px;
  }
  .acat-preview-img-box {
    position: relative;
    width: 100%;
    max-width: 230px;
    animation: acatPreviewIn 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes acatPreviewIn {
    from { opacity: 0; transform: scale(0.9); }
    to   { opacity: 1; transform: scale(1); }
  }
  .acat-preview-img {
    width: 100%;
    height: 210px;
    object-fit: contain;
    background: #fff;
    border-radius: 16px;
    padding: 12px;
    box-shadow: 0 10px 24px rgba(124, 45, 18, 0.18);
    display: block;
  }
  .acat-remove-btn {
    position: absolute;
    top: -12px;
    right: -12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid #fff;
    background: #ef4444;
    color: #fff;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(239,68,68,0.5);
    transition: transform 0.25s ease, background 0.2s ease;
  }
  .acat-remove-btn:hover {
    background: #dc2626;
    transform: scale(1.15) rotate(90deg);
  }
  .acat-preview-name {
    font-weight: 700;
    color: #1e293b;
    margin-top: 12px;
    text-align: center;
    word-break: break-word;
  }
  .acat-remove-wide {
    width: 100%;
    margin-top: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border: 1px solid #fecaca;
    border-radius: 12px;
    padding: 8px 14px;
    font-size: 0.82rem;
    font-weight: 700;
    color: #b91c1c;
    background: #fff;
    cursor: pointer;
    transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .acat-remove-wide:hover {
    transform: translateY(-2px);
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.4);
  }
  .acat-preview-empty {
    width: 100%;
    max-width: 230px;
    height: 210px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #a8a29e;
    border-radius: 16px;
    background: #fff;
    border: 1px solid #f6eee2;
  }
  .acat-preview-empty-ico {
    font-size: 2.6rem;
    animation: acatIconBob 3.4s ease-in-out infinite;
  }

  /* ===== Category cards ===== */
  .acat-card {
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    border: 2px solid transparent !important;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    animation: acatCardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 60ms);
  }
  @keyframes acatCardIn {
    from { opacity: 0; transform: translateY(22px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .acat-card:hover {
    transform: translateY(-8px);
    border-color: color-mix(in srgb, var(--c-accent, #ea580c) 45%, transparent) !important;
    box-shadow: 0 24px 44px color-mix(in srgb, var(--c-accent, #ea580c) 28%, transparent) !important;
  }
  @supports not (border-color: color-mix(in srgb, red 10%, blue)) {
    .acat-card:hover {
      border-color: rgba(234, 88, 12, 0.35) !important;
      box-shadow: 0 24px 44px rgba(194, 65, 12, 0.22) !important;
    }
  }
  /* The card currently loaded into the form keeps a brass rim */
  .acat-card.is-editing {
    border-color: #fbbf24 !important;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.2) !important;
  }

  .acat-card-img-wrap {
    position: relative;
    overflow: hidden;
  }
  .acat-card-img {
    height: 180px;
    width: 100%;
    object-fit: cover;
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .acat-card:hover .acat-card-img {
    transform: scale(1.12) rotate(1.4deg);
  }
  .acat-card-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.1) 55%, rgba(15,23,42,0) 100%);
  }
  /* Colour rises from the bottom on hover — clipped by the wrap, so it can
     never spill onto a neighbouring card */
  .acat-card-wash {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(to top,
      color-mix(in srgb, var(--c-accent, #ea580c) 62%, transparent) 0%,
      transparent 68%);
    transform: translateY(100%);
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .acat-card:hover .acat-card-wash { transform: translateY(0); }
  @supports not (background: color-mix(in srgb, red 10%, blue)) {
    .acat-card-wash { background: linear-gradient(to top, rgba(234,88,12,0.6), transparent 68%); }
  }

  .acat-card-title {
    position: absolute;
    left: 16px;
    bottom: 12px;
    right: 16px;
    z-index: 2;
    margin: 0;
    color: #fff;
    font-weight: 800;
    text-shadow: 0 2px 8px rgba(0,0,0,0.5);
    text-transform: capitalize;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .acat-card:hover .acat-card-title { transform: translateY(-3px); }

  /* ===== Stats strip ===== */
  .acat-stat {
    background: #fffdf7;
    border: 1px solid #f6eee2;
    border-radius: 12px;
    padding: 8px 4px;
    height: 100%;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      border-color 0.25s ease, background 0.25s ease;
  }
  .acat-card:hover .acat-stat {
    background: #fff;
    border-color: color-mix(in srgb, var(--c-accent, #ea580c) 30%, transparent);
  }
  .acat-stat:hover { transform: translateY(-3px); }
  .acat-stat-num {
    font-weight: 800;
    font-size: 0.95rem;
    line-height: 1.1;
    color: #1e293b;
  }
  .acat-stat-num.is-green { color: #16a34a; }
  .acat-stat-num.is-blue  { color: #2563eb; }
  .acat-stat-label {
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #a8a29e;
    margin-top: 2px;
  }

  /* ===== Card actions ===== */
  .acat-view-btn {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    border-radius: 12px;
    padding: 10px 16px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;
    background: linear-gradient(135deg, var(--c-from, #c2410c), var(--c-to, #ea580c));
    box-shadow: 0 8px 20px color-mix(in srgb, var(--c-accent, #ea580c) 38%, transparent);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, filter 0.25s ease;
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .acat-view-btn { box-shadow: 0 8px 20px rgba(234, 88, 12, 0.35); }
  }
  .acat-view-btn:hover { transform: translateY(-3px); filter: brightness(1.08); }
  .acat-view-btn:active { transform: translateY(-1px) scale(0.97); }
  .acat-view-ico { transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
  .acat-view-btn:hover .acat-view-ico { transform: scale(1.25); }

  .acat-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border-radius: 12px;
    padding: 8px 12px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: #fff;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease;
  }
  .acat-act:hover { transform: translateY(-2px); }
  .acat-act:active { transform: translateY(0) scale(0.96); }
  .acat-act-ico { display: inline-block; }

  .acat-act-edit {
    border: 1px solid #fcd34d;
    color: #b45309;
  }
  .acat-act-edit:hover {
    color: #431407;
    border-color: transparent;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 10px 22px rgba(245, 158, 11, 0.42);
  }
  .acat-act-edit:hover .acat-act-ico {
    animation: acatWiggle 0.6s ease-in-out infinite;
  }
  @keyframes acatWiggle {
    0%, 100% { transform: rotate(0deg); }
    32%      { transform: rotate(-17deg); }
    66%      { transform: rotate(13deg); }
  }

  .acat-act-del {
    border: 1px solid #fecaca;
    color: #b91c1c;
  }
  .acat-act-del:hover {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.42);
  }
  .acat-act-del:hover .acat-act-ico,
  .acat-remove-wide:hover .acat-act-ico {
    animation: acatShake 0.55s ease-in-out infinite;
  }
  @keyframes acatShake {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    30%      { transform: translateX(-2px) rotate(-14deg); }
    65%      { transform: translateX(2px) rotate(11deg); }
  }

  .acat-empty-icon {
    font-size: 3rem;
    animation: acatIconBob 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .acat-hero,
    .acat-hero::before,
    .acat-hero::after,
    .acat-hero-aurora,
    .acat-mote,
    .acat-hero-icon,
    .acat-hero-title-text,
    .acat-form-header,
    .acat-form-header::after,
    .acat-form-icon,
    .acat-card,
    .acat-preview-img-box,
    .acat-preview-empty-ico,
    .acat-act-edit:hover .acat-act-ico,
    .acat-act-del:hover .acat-act-ico,
    .acat-remove-wide:hover .acat-act-ico,
    .acat-empty-icon { animation: none !important; }
    .acat-motes { display: none; }
    .acat-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminCategoriesPage;
